import * as S from './styled'

import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { t } from 'i18next'

import { focusField, getErrorClass, validateField } from '@/util/FormStateUtil'
import { loadingStatus as setLoadingAction } from '@/core/store/client/actions'
import { setUserLogin, clearUser } from '@/core/store/user/actions'
import { useCommonState } from '@/core/context/useContext'
import { usePopup } from '@/core/context/useWindow'
import Error from '@/components/common/Error'
import api from '@/core/services/api'
import useFormData from '@/core/hooks/useFormData'
import iconsSprite from '@/assets/icons.svg'
import GoldRegister from '@/components/gold/GoldRegister'

/**
 * 로그인 팝업
 */
export default function Login({ isMobile = false }) {
  const dispatch = useDispatch()
  const [hide, setHide] = useState(false)
  const [loginErr, setLoginErr] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { openPopup } = usePopup()
  const { inputError, setInputError, handleApiErrors } = useCommonState()

  const idRef = useRef(null)
  const passwordRef = useRef(null)

  /** 비밀번호 표시/숨김 토글 */
  const togglePassword = () => {
    setHide(prev => !prev)
  }

  /** 로그인 API 호출 */
  const submitLogin = () => {
    if (isLoading) return

    setIsLoading(true)
    dispatch(setLoadingAction(true))

    const getError = {}
    validateField(getValue.formValue, 'id', t('error.input_id'), getError)
    validateField(getValue.formValue, 'password', t('error.input_password'), getError)

    if (!getValue.formValue.id) {
      focusField(idRef)
    } else if (!getValue.formValue.password) {
      focusField(passwordRef)
    }

    dispatch(clearUser())

    api
      .post('/auth/login-user', { ...getValue.formValue })
      .then((res) => {
        dispatch(setUserLogin(res.data.content))
        window.location.reload()
      })
      .catch((err) => {
        const { status, data } = err.response
        if (err.response && status === 400) {
          if (data.errors === 'Failed_login') {
            openPopup({
              title: t('alert.fail_login'),
              content: t('alert.fail_login_content1'),
            })
          } else if (data.errors === 'signup_wait_limit') {
            setInputError(prev => ({
              ...prev,
              password: t('error.signup_wait_limit'),
            }))
            setLoginErr(true)
          } else if (data.errors === 'Failed_user') {
            setInputError(prev => ({
              ...prev,
              password: t('error.invalid_user'),
            }))
            setLoginErr(true)
          } else if (data.errors === 'Failed_id') {
            setInputError(prev => ({
              ...prev,
              id: t('error.invalid_id'),
            }))
            setLoginErr(true)
          }
        }
        console.error(err)
        handleApiErrors(err.response.data.errors)
      })
      .finally(() => {
        setIsLoading(false)
        dispatch(setLoadingAction(false))
      })

    setInputError(getError)
  }

  const getValue = useFormData(
    { id: '', password: '' },
    submitLogin,
  )

  const idErr = getErrorClass(inputError, 'id')
  const pwdErr = !loginErr && getErrorClass(inputError, 'password')

  /**
   * 폼 제출 핸들러
   * @param {Event} e
   */
  const handleSubmit = (e) => {
    e.preventDefault()
    submitLogin()
  }

  // ver2(gold) 로그인 — PC: 각진 어두운 입력칸 2개 + 노란 그라디언트 버튼(→).
  //   모바일: 입력칸 위 금색 라벨, 둥근 입력칸, 노란 알약 버튼. 틀(상자·로고·제목)은 useWindow 의 grandLogin.
  return (
    <S.Form noValidate onSubmit={handleSubmit} $mobile={isMobile}>
      {isMobile && <S.Label htmlFor="gold-login-id">{t('gold.account')}</S.Label>}
      <S.Field $mobile={isMobile} $error={!!idErr}>
        <S.Input
          id="gold-login-id"
          $mobile={isMobile}
          ref={idRef}
          type="text"
          name="id"
          autoComplete="username"
          placeholder={t('gold.account')}
          value={getValue.formValue.id}
          onChange={(e) => getValue.onChange('id', e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submitLogin()
            }
          }}
          autoFocus
        />
      </S.Field>
      <Error errors={inputError} name="id" padL="4px" />

      {isMobile && <S.Label htmlFor="gold-login-pw">{t('gold.password')}</S.Label>}
      <S.Field $mobile={isMobile} $error={!!pwdErr}>
        <S.Input
          id="gold-login-pw"
          $mobile={isMobile}
          ref={passwordRef}
          type={hide ? 'text' : 'password'}
          name="password"
          autoComplete="current-password"
          placeholder={t('gold.password')}
          value={getValue.formValue.password}
          onChange={(e) => getValue.onChange('password', e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submitLogin()
            }
          }}
        />
        <S.EyeToggle
          $mobile={isMobile}
          onClick={togglePassword}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              togglePassword()
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={hide ? 'hide password' : 'show password'}
        >
          <S.IconSvg viewBox={hide ? '0 0 488.85 488.85' : '0 0 24 24'}>
            <use href={`${iconsSprite}#${hide ? 'ico-eyeon' : 'ico-eyeoff'}`} />
          </S.IconSvg>
        </S.EyeToggle>
      </S.Field>
      <Error errors={inputError} name="password" padL="4px" />

      {/* gold PC 로그인 창의 「パスワードを忘れる」 — 더미(비밀번호 찾기 기능 없음, 준비 중 안내) */}
      {!isMobile && (
        <S.Forget type="button" onClick={() => openPopup({ title: t('gold.forgot'), content: t('gold.comingSoon') })}>
          {t('gold.forgot')}
        </S.Forget>
      )}

      <S.LoginBtn $mobile={isMobile} disabled={isLoading}>
        {t('loginForm.login')}
        {!isMobile && <S.Arrow aria-hidden="true">→</S.Arrow>}
      </S.LoginBtn>

      {/* gold PC 로그인 창 아래 회원가입 안내 — 항목은 사이트 가입 항목 설정(user_signup_field)대로 */}
      {!isMobile && (
        <S.SignUpLine>
          {t('gold.noAccount')}
          <button type="button" onClick={() => openPopup({ raw: true, content: <GoldRegister /> })}>{t('gold.createNow')}</button>
        </S.SignUpLine>
      )}
    </S.Form>
  )
}
